import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { brokerService, getErrorMessage } from '@/services';
import type {
  BrokerFilters,
  BrokerCreateRequest,
  POSP,
  POSPComplianceUpdate,
  POSPCreateRequest,
  POSPFilters,
} from '@/types';
import type { POSPPolicyFilters } from '@/services/brokers';

export function useBrokers(page: number = 1, filters?: BrokerFilters) {
  return useQuery({
    queryKey: ['brokers', page, filters],
    queryFn: () => brokerService.getBrokers(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

export function useBroker(id: string) {
  return useQuery({
    queryKey: ['broker', id],
    queryFn: () => brokerService.getBroker(id),
    enabled: !!id,
  });
}

export function useCreateBroker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BrokerCreateRequest) => brokerService.createBroker(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      toast.success('Broker created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Labels for the three states a country admin can put a broker in. */
export const BROKER_STATUS_LABELS = {
  active: 'Active',
  suspended: 'Suspended',
  blocked: 'Blocked',
} as const;

/** The ACTION that puts a broker into each state.
 *
 *  Distinct from BROKER_STATUS_LABELS on purpose: a button says what it will
 *  do ("Suspend"), a badge says what is true ("Suspended"). Using the state
 *  name on the button produced "Suspended Alpha Brokers?" as a confirmation
 *  title, which reads as a statement rather than a question. */
export const BROKER_STATUS_ACTIONS = {
  active: 'Reactivate',
  suspended: 'Suspend',
  blocked: 'Block',
} as const;

/** What each state actually does, said plainly. Shown in the confirmation, so
 *  nobody suspends an organisation thinking it only changes a badge. */
export const BROKER_STATUS_EFFECTS = {
  active:
    'The broker and their whole team — employees and POSPs — can log in and trade again.',
  suspended:
    'Nobody at this broker can log in: not the admin, not employees, not POSPs. Existing policies are untouched.',
  blocked:
    'Nobody at this broker can log in: not the admin, not employees, not POSPs. Existing policies are untouched.',
} as const;

export type BrokerSettableStatus = keyof typeof BROKER_STATUS_LABELS;

/**
 * Activate, suspend or block a broker organisation.
 *
 * The toast names the status the SERVER stored, read back off the response
 * rather than off what was asked for — if the two ever disagree the admin sees
 * the truth rather than a confirmation of their own click.
 */
export function useSetBrokerStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, status, reason }: { id: string; status: BrokerSettableStatus; reason?: string }) =>
      brokerService.setBrokerStatus(id, status, reason),
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      queryClient.invalidateQueries({ queryKey: ['broker', variables.id] });
      const stored = result?.status as BrokerSettableStatus | undefined;
      toast.success(
        stored
          ? `Broker set to ${BROKER_STATUS_LABELS[stored] ?? stored}.`
          : 'Broker status updated.',
      );
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useUpdateBroker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<BrokerCreateRequest> }) =>
      brokerService.updateBroker(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['brokers'] });
      queryClient.invalidateQueries({ queryKey: ['broker', variables.id] });
      toast.success('Broker updated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useBrokerEmployees(brokerId: string) {
  return useQuery({
    queryKey: ['broker-employees', brokerId],
    queryFn: () => brokerService.getBrokerEmployees(brokerId),
    enabled: !!brokerId,
  });
}

export function useBrokerUsers(brokerId: string) {
  return useQuery({
    queryKey: ['broker-users', brokerId],
    queryFn: () => brokerService.getBrokerUsers(brokerId),
    enabled: !!brokerId,
  });
}

export function useMapUserToBroker() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ brokerId, userId, role, isPrimary }: { brokerId: string; userId: string; role: string; isPrimary?: boolean }) =>
      brokerService.mapUserToBroker(brokerId, userId, role, isPrimary),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['broker-users', variables.brokerId] });
      toast.success('User mapped to broker successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePOSPs(page: number = 1, filters?: POSPFilters) {
  return useQuery({
    queryKey: ['posps', page, filters],
    queryFn: () => brokerService.getPOSPs(page, filters),
    placeholderData: (previousData) => previousData,
  });
}

/** The server's cap on `page_size` (core.pagination.StandardResultsSetPagination). */
const POSP_LOOKUP_PAGE_SIZE = 100;
/** Hard stop on the page walk: 50 x 100 = 5,000 agents, far beyond any broker. */
const POSP_LOOKUP_MAX_PAGES = 50;

/**
 * Fetch a flat list of EVERY active POSP for the current broker (dropdown use).
 * Backend scopes /brokers/posps/ to the requester's broker, so no explicit
 * broker filter is needed.
 *
 * This used to return PAGE ONE only — the first 20 agents (REST_FRAMEWORK
 * PAGE_SIZE) — so a broker with more active agents than that could not pick
 * the rest from a plain dropdown (Policy.Online has 35), and the Commissions
 * ledger grew free-text "Search agents" boxes to work around it. It now asks
 * for the server's maximum page size (100) and follows `next` to the end, so
 * the list is complete and no picker needs a search box to reach agent 21.
 *
 * `search` is still optional and forwarded to the endpoint's SearchFilter
 * (POSPViewSet.search_fields — posp_code / email / first+last name) for the
 * callers with a type-ahead (PolicyCreatePage, PolicyDetailPage). It is part
 * of the query key so each term caches separately, and the previous list is
 * kept as placeholder data so the popover doesn't blink empty between
 * keystrokes.
 */
export function usePOSPsLookup(search?: string) {
  const term = (search || '').trim();
  return useQuery({
    queryKey: ['posps', 'lookup', 'active', term],
    queryFn: async () => {
      const all: POSP[] = [];
      for (let page = 1; page <= POSP_LOOKUP_MAX_PAGES; page += 1) {
        const result = await brokerService.getPOSPs(page, {
          status: 'active',
          search: term || undefined,
          page_size: POSP_LOOKUP_PAGE_SIZE,
        });
        all.push(...(result.results || []));
        if (!result.next) break;
      }
      return all;
    },
    staleTime: 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

export function useCreatePOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: POSPCreateRequest) => brokerService.createPOSP(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      toast.success('POSP created successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function usePOSP(id: string) {
  return useQuery({
    queryKey: ['posp', id],
    queryFn: () => brokerService.getPOSP(id),
    enabled: !!id,
  });
}

/**
 * P1 — the agent's compliance & training record.
 *
 * One query key for both audiences, because it is one row: the Broker Admin's
 * Training & Certification card and the agent's own Certifications card read
 * the same endpoint, so a stage the broker just moved cannot show stale on the
 * agent's screen after a refetch.
 */
export function usePOSPCompliance(pospId: string | undefined) {
  return useQuery({
    queryKey: ['posp', pospId, 'compliance'],
    queryFn: () => brokerService.getPOSPCompliance(pospId!),
    enabled: !!pospId,
  });
}

/** Step the lifecycle and/or record the facts. Broker staff only. */
export function useAdvancePOSPCompliance(pospId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: POSPComplianceUpdate) =>
      brokerService.advancePOSPCompliance(pospId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['posp', pospId, 'compliance'] });
      // `training_completed` moves with the final stage, so the POSP row the
      // profile cards read is stale too.
      queryClient.invalidateQueries({ queryKey: ['posp', pospId] });
      queryClient.invalidateQueries({ queryKey: ['posp', 'me'] });
      // Say what was actually written. A blanket "Saved" over a request that
      // stored nothing is the bug this codebase repeats most.
      if (data.changed_fields.length === 0) {
        toast('Nothing changed — the values sent were already recorded.');
      } else if (data.changed_fields.includes('compliance_status')) {
        toast.success(`Moved to “${data.compliance.compliance_status_display}”.`);
      } else {
        toast.success('Compliance details recorded.');
      }
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });
}

/** POSP-6: the logged-in POSP's own profile for My Portal. */
export function useMyPOSP(enabled: boolean = true) {
  return useQuery({
    queryKey: ['posp', 'me'],
    queryFn: () => brokerService.getMyPOSP(),
    enabled,
  });
}

export function usePospChannel(id: string) {
  return useQuery({
    queryKey: ['pospChannel', id],
    queryFn: () => brokerService.getPOSPChannel(id),
    enabled: !!id,
  });
}

export function usePOSPPolicies(
  pospId: string,
  page: number = 1,
  filters?: POSPPolicyFilters,
) {
  return useQuery({
    queryKey: ['posp-policies', pospId, page, filters],
    queryFn: () => brokerService.getPOSPPolicies(pospId, page, filters),
    enabled: !!pospId,
    placeholderData: (previousData) => previousData,
  });
}

/** Real FY (or all-time) commission aggregate for the POSP holistic profile —
 *  ONE backend aggregate query, NOT a client-side sum of the current policies
 *  page. `filters` should mirror whatever is passed to usePOSPPolicies so the
 *  headline figure and the table below it always describe the same window. */
export function usePOSPEarningsSummary(
  pospId: string,
  filters?: POSPPolicyFilters,
) {
  return useQuery({
    queryKey: ['posp-earnings-summary', pospId, filters],
    queryFn: () => brokerService.getPOSPEarningsSummary(pospId, filters),
    enabled: !!pospId,
  });
}

export function usePOSPTargets(pospId: string) {
  return useQuery({
    queryKey: ['posp-targets', pospId],
    queryFn: () => brokerService.getPOSPTargets(pospId),
    enabled: !!pospId,
  });
}

export function usePOSPPayouts(pospId: string) {
  return useQuery({
    queryKey: ['posp-payouts', pospId],
    queryFn: () => brokerService.getPOSPPayouts(pospId),
    enabled: !!pospId,
  });
}

export function useActivatePOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => brokerService.activatePOSP(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      queryClient.invalidateQueries({ queryKey: ['posp', id] });
      toast.success('POSP activated successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/** Issues (or resets) a POSP's login. The one-time temp password is returned
 *  in the mutation result — deliberately NOT toasted, so the caller can show
 *  it in a "copy now, shown once" dialog instead of a transient toast. */
export function useCreatePOSPLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => brokerService.createPOSPLogin(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['posp', id] });
      // M-05: the channel detail page reads `password_set` off the unified
      // row to label the button; a first login must flip it to a reset.
      queryClient.invalidateQueries({ queryKey: ['unifiedChannel'] });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useSuspendPOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      brokerService.suspendPOSP(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      queryClient.invalidateQueries({ queryKey: ['posp', variables.id] });
      toast.success('POSP suspended');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useTerminatePOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      brokerService.terminatePOSP(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      queryClient.invalidateQueries({ queryKey: ['posp', variables.id] });
      toast.success('POSP terminated');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useSubmitPOSPDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      brokerService.submitPOSPDocuments(id, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posp', variables.id] });
      toast.success('Documents submitted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useApprovePOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => brokerService.approvePOSP(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      queryClient.invalidateQueries({ queryKey: ['posp', id] });
      toast.success('POSP approved successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useRejectPOSP() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      brokerService.rejectPOSP(id, reason),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posps'] });
      queryClient.invalidateQueries({ queryKey: ['posp', variables.id] });
      toast.success('POSP rejected');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

export function useResubmitPOSPDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, formData }: { id: string; formData: FormData }) =>
      brokerService.resubmitPOSPDocuments(id, formData),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['posp', variables.id] });
      toast.success('Documents resubmitted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
